/**
 * Inverse Challenge Strategy Implementation
 * Handles the "X is Yth in the scale of..." challenge format
 */

import { Challenge, ChallengeType, ChallengeStrategy } from './challenge-types.js';
import { MusicTheory } from './music-theory.js';

export class InverseChallengeStrategy implements ChallengeStrategy {
  /**
   * Generate a new inverse challenge
   * @param key - Optional specific key to use, otherwise random
   * @returns Generated inverse challenge
   */
  generateChallenge(key?: string): Challenge {
    const challengeKey = key || MusicTheory.getRandomKey();
    const degree = MusicTheory.getRandomDegree();
    const note = MusicTheory.getScaleDegree(challengeKey, degree);

    return {
      type: ChallengeType.INVERSE,
      key: challengeKey,
      degree,
      note,
      correctAnswer: challengeKey, // For inverse mode, the answer is the key
      timestamp: Date.now()
    };
  }

  /**
   * Format the challenge text for display in the UI
   * @param challenge - The challenge to format
   * @returns Display text string in "X is Yth in the scale of..." format
   */
  formatDisplayText(challenge: Challenge): string {
    const ordinalSuffix = this.getOrdinalSuffix(challenge.degree);
    return `${challenge.note} is ${challenge.degree}${ordinalSuffix} in the scale of...`;
  }

  /**
   * Format the challenge text for speech synthesis (TTS)
   * @param challenge - The challenge to format
   * @returns Speech text string with proper pronunciation
   */
  formatSpeechText(challenge: Challenge): string {
    const ordinalSuffix = this.getOrdinalSuffix(challenge.degree);
    const speakableNote = MusicTheory.getSpeakableNote(challenge.note);
    return `${speakableNote} is ${challenge.degree}${ordinalSuffix} in the scale of`;
  }

  /**
   * Validate a user's answer against the challenge
   * @param challenge - The challenge being answered
   * @param answer - User's answer (key name)
   * @returns True if the answer is correct
   */
  validateAnswer(challenge: Challenge, answer: string | null): boolean {
    return MusicTheory.isValidKeyAnswer(challenge.correctAnswer, answer);
  }

  /**
   * Get the correct answer in speech format for TTS feedback
   * @param challenge - The challenge that was answered incorrectly
   * @returns Speech text for the correct answer
   */
  getCorrectAnswerSpeech(challenge: Challenge): string {
    return `Correct answer: ${MusicTheory.getSpeakableKeyName(challenge.correctAnswer)}`;
  }

  /**
   * Get the ordinal suffix for a number (e.g., 2 -> "nd", 3 -> "rd")
   * @param num - The number to get the suffix for
   * @returns The ordinal suffix
   */
  private getOrdinalSuffix(num: number): string {
    const lastDigit = num % 10;
    const lastTwoDigits = num % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return 'th';
    }

    switch (lastDigit) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }
}